import { HTTP } from '@community_dev/requests'
import { SnakeCasedPropertiesDeep } from 'type-fest'

import { ENDPOINTS } from 'constants/endpoints'
import { CommunityClient } from 'types/communityClient'
import { request } from 'utils/api'

type PostLoginRequest = {
  email: string
  password: string
  redirect_uri: string
  client_id: string
  response_type: string
}

type PostLoginResponse = {
  token: string
  clients: SnakeCasedPropertiesDeep<CommunityClient[]>
}

type JsonResponse<T> = Response & {
  json(): Promise<T>
}

export function postLogin(auth: PostLoginRequest): Promise<JsonResponse<PostLoginResponse>> {
  return request(ENDPOINTS.OAUTH.LOGIN, {
    body: auth,
    method: HTTP.POST,
    noAuth: true,
    rawResponse: true,
  })
}

type MfaPostResponse = {
  id: string
  token: string
  clients: CommunityClient[]
}

type MfaPostRequest = {
  totp: string
  client_id: string
  mfaToken: string
}

export function postMfaCode({ totp, client_id, mfaToken }: MfaPostRequest): Promise<MfaPostResponse> {
  return request(ENDPOINTS.OAUTH.MFA, {
    method: HTTP.POST,
    body: { totp, client_id },
    headers: {
      Authorization: `Bearer ${mfaToken}`,
    },
    skipAuthCheck: true,
  })
}

export type PermissionCategory = {
  title: string
  description: string
  toolCount: number
  access: 'read' | 'write'
}

export type PermissionNotice = {
  headline: string
  summary: string
  cautions: string[]
  categories: PermissionCategory[]
}

type IntegrationClientResponse = {
  integrationClientImage: string
  integrationClientName: string
  permissionNotice?: PermissionNotice | null
}

type AuthorizeClientResponse = {
  redirectUri: string
}

export function getIntegrationClient(id: string): Promise<IntegrationClientResponse> {
  return request(ENDPOINTS.OAUTH.INTEGRATION_CLIENT.replace(':id', id), {
    noAuth: true,
  })
}

export async function authorizeClient(
  token: string,
  communityClient: CommunityClient,
  clientId: string,
  state: string,
  redirectUri: string,
  // PKCE (RFC 7636). Optional — forwarded only when the connector initiated the
  // flow with a code_challenge in the authorize URL. Non-PKCE integrations omit
  // them and the body is unchanged.
  codeChallenge?: string,
  codeChallengeMethod?: string,
): Promise<AuthorizeClientResponse> {
  const res = await request(ENDPOINTS.OAUTH.AUTHORIZE_CLIENT, {
    token,
    body: {
      community_client_id: communityClient.clientId,
      client_id: clientId,
      state: state,
      redirect_uri: redirectUri,
      ...(codeChallenge ? { code_challenge: codeChallenge } : {}),
      ...(codeChallengeMethod ? { code_challenge_method: codeChallengeMethod } : {}),
    },
    method: HTTP.POST,
  })
  window.location.href = res.redirectUri
  return res
}
